import { Zap, Calendar, MessageSquare, Layout, Globe, ArrowRight } from "lucide-react";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import Magnetic from "@/components/Magnetic";

const offerings = [
  {
    number: "01",
    title: "Small Business Websites",
    tags: ["High Speed", "Optimized", "SEO Ready"],
    desc: "We build ultra-fast AI-native interfaces. Minimalist infrastructure for maximum speed and intelligent flows that connect you with your audience.",
    icon: Layout,
    color: "pulse",
    span: "md:col-span-2",
  },
  {
    number: "02",
    title: "Seamless Scheduling",
    tags: ["Booking", "iCal/Google", "Automated"],
    desc: "Integrate Pulse Chat directly into your workflow. Let AI handle the heavy lifting of scheduling and customer management.",
    icon: Calendar,
    color: "electric",
    span: "md:col-span-1",
  },
  {
    number: "03",
    title: "AI Navigation Bots",
    tags: ["24/7 Support", "Smart Search", "OpenAI"],
    desc: "Sophisticated AI assistants that understand your business, answer complex queries, and drive meaningful engagement 24/7.",
    icon: MessageSquare,
    color: "electric",
    span: "md:col-span-1",
  },
  {
    number: "04",
    title: "Ready-to-Scale Infrastructure",
    tags: ["Cloud", "Security", "Reliable"],
    desc: "Enterprise-grade AI infrastructure simplified for you. We handle the data science and hosting so you can focus on the vision.",
    icon: Globe,
    color: "pulse",
    span: "md:col-span-2",
  },
];

const OfferingCard = ({ item, index }: { item: any; index: number }) => {
  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);

  function handleMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: React.MouseEvent) {
    let { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onMouseMove={handleMouseMove}
      className={`group relative overflow-hidden rounded-[2rem] border border-white/5 bg-white/5 transition-all duration-500 hover:border-pulse/30 p-6 md:p-8 font-body ${item.span}`}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-[2.5rem] opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              hsl(var(--${item.color === 'pulse' ? 'pulse' : 'electric'}) / 0.15),
              transparent 80%
            )
          `,
        }}
      />

      {/* Video-preview scan shimmer on hover */}
      <div
        className="pointer-events-none absolute inset-0 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden"
      >
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            background: "linear-gradient(90deg, transparent 0%, hsl(var(--pulse) / 0.08) 48%, hsl(var(--pulse) / 0.15) 50%, hsl(var(--pulse) / 0.08) 52%, transparent 100%)",
            animation: "shimmer-scan 2.5s ease-in-out infinite",
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between mb-6">
          <div className={`w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-${item.color === 'pulse' ? 'pulse' : 'electric'}`}>
            <item.icon size={24} />
          </div>
          <span className={`text-[9px] text-${item.color === 'pulse' ? 'pulse' : 'electric'} font-bold tracking-[0.3em] uppercase opacity-50`}>
            Step {item.number}
          </span>
        </div>

        <div className="flex-1">
          <h3 className="text-2xl font-display text-foreground group-hover:text-pulse transition-colors mb-3">
            {item.title}
          </h3>
          <p className="text-foreground-muted text-sm leading-relaxed max-w-sm mb-8">
            {item.desc}
          </p>
        </div>

        <div className="pt-6 border-t border-white/5 flex items-center justify-between">
          <div className="flex gap-2">
            {item.tags.map((tag: string) => (
              <span key={tag} className="text-[9px] uppercase tracking-widest px-3 py-1 bg-white/5 rounded-full border border-white/5 text-pulse/60">
                {tag}
              </span>
            ))}
          </div>
          <Magnetic strength={0.2}>
            <button className="text-pulse opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
              <ArrowRight size={20} />
            </button>
          </Magnetic>
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="py-8 relative overflow-hidden bg-background">
      {/* Large background decorative text */}
      <div className="absolute -left-20 bottom-20 text-[20rem] font-display font-black text-white/[0.02] select-none pointer-events-none uppercase">
        Offer
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 text-center md:text-left">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-body text-[10px] uppercase tracking-[0.5em] text-pulse/80 bg-pulse/5 px-4 py-1.5 rounded-full border border-pulse/10">Services</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mt-4 leading-tight">
              Intelligent. Bold. <br />
              <em className="text-gradient-pulse not-italic">Automated.</em>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="md:max-w-sm font-body text-foreground-muted text-sm mt-6 md:mt-0 leading-relaxed"
          >
            We eliminate the noise of digital communication so you can focus on building your legacy.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {offerings.map((item, i) => (
            <OfferingCard key={item.number} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
